// Copyright (c) StrangeBee 2024

import React, { ReactElement, useCallback, useContext, useMemo } from 'react';
import client from '@Client';
import useListQuery from '@Hooks/useListQuery';
import { createContext, ReactNode } from 'react';
import {
    TagOutput,
    TaxonomyOutput,
    CustomStatusOutput,
    PossibleNamedQuery,
    UserOutput,
    ResponderOutput,
    AnalyzerOutput,
    CustomFieldOutput,
} from 'thehive-sdk';
import { computeListSelector } from '@Organisms/Organisation/selector';
import { computeListSelector as computeProfileListSelector } from '@Organisms/Profile/selector';
import { computeListSelector as computeUserListSelector } from '@Organisms/User/selector';
import { computeListSelector as computeCustomTagListSelector } from '@Organisms/CustomTag/selector';
import { useCurrentUser } from '@Queries/userQueries';
import { RefetchType, useGenericQuery } from '@Queries/genericQueries';
import { UseListQueryParams } from '@Hooks/useCustomQuery';
import { StatusContext } from './StatusProvider';
import { formatTags } from '@Helpers/tags';

interface ScopeOutput {
    taxonomy: { list: TaxonomyOutput[]; refetch: RefetchType<TaxonomyOutput[] | undefined> };
    coloredTags: Record<string, string>;
    responderList: ResponderOutput[];
    analyzerList: AnalyzerOutput[];
    customFieldList: CustomFieldOutput[];
    customStatus: {
        alert: {
            list: CustomStatusOutput[];
            refetch: RefetchType<CustomStatusOutput[]>;
            getStatus: (name: string) => CustomStatusOutput | null;
        };
        case: {
            list: CustomStatusOutput[];
            refetch: RefetchType<CustomStatusOutput[]>;
            getStatus: (name: string) => CustomStatusOutput | null;
        };
    };
}

export const ScopeContext = createContext<ScopeOutput>({
    taxonomy: {
        list: [],
        refetch: async () => [] as TaxonomyOutput[] as any,
    },
    coloredTags: {},
    responderList: [],
    analyzerList: [],
    customFieldList: [],
    customStatus: {
        alert: {
            list: [],
            refetch: async () => [] as CustomStatusOutput[] as any,
            getStatus: () => null,
        },
        case: {
            list: [],
            refetch: async () => [] as CustomStatusOutput[] as any,
            getStatus: () => null,
        },
    },
});

const computeParamsCustomStatusQuery = ({
    name,
    nameRef,
    fetchOnStart,
}: {
    name: PossibleNamedQuery;
    nameRef: string;
    fetchOnStart: boolean;
}): UseListQueryParams => ({
    queryParams: {
        selector: {
            head: [{ name }],
            extraData: ['canDelete'],
            name: nameRef,
        },
        sort: ['-stage', '+value'],
    },
    controller: client.CustomStatusController,
    fetchOnStart,
    manual: true,
});

interface ScopeProviderProps {
    children: ReactNode;
}

export const ScopeProvider = ({ children }: ScopeProviderProps): ReactElement => {
    const { data: currentUser } = useCurrentUser();

    const {
        config: { freeTagDefaultColour },
    } = useContext(StatusContext);

    useListQuery<UserOutput[]>({
        queryParams: {
            selector: computeUserListSelector({ orgId: currentUser?.organisation || '' }),
        },
        controller: client.UserController,
        fetchOnStart: !!currentUser,
        manual: true,
    });

    const { data: customTagList = [] } = useListQuery<TagOutput[]>({
        queryParams: {
            selector: computeCustomTagListSelector(),
            filters: [{ field: 'colour', operator: 'not', values: [freeTagDefaultColour], type: 'string' }],
        },
        controller: client.CustomTagController,
        fetchOnStart: !!currentUser,
        manual: true,
    });

    const { data: taxonomyList, refetch: refetchTaxonomy } = useListQuery<TaxonomyOutput[]>({
        queryParams: {
            selector: {
                head: [{ name: 'listTaxonomy' }],
                name: 'get-all-taxonomies',
            },
            sort: ['+namespace'],
        },
        controller: client.TaxonomyController,
        manual: true,
        fetchOnStart: !!currentUser,
    });

    const { data: responderList = [] } = useGenericQuery<ResponderOutput[]>({
        fetchCallback: () => client.CortexController.listResponder(),
        key: ['get-all-responders'],
        manual: true,
        fetchOnStart: !!currentUser,
    });

    const { data: analyzerList = [] } = useGenericQuery<AnalyzerOutput[]>({
        fetchCallback: () => client.AnalyzerController.listAnalyzer(),
        key: ['get-all-analyzer'],
        manual: true,
        fetchOnStart: !!currentUser,
    });

    const { data: customFieldList = [] } = useListQuery<CustomFieldOutput[]>({
        queryParams: {
            selector: {
                head: [{ name: 'listCustomField' }],
                name: `get-custom-field`,
            },
            sort: ['-_createdAt'],
        },
        controller: client.CustomFieldController,
        manual: true,
        fetchOnStart: !!currentUser,
    });

    const { data: customAlertStatusList = [], refetch: refetchAlertCustomStatusList } = useListQuery<
        CustomStatusOutput[]
    >(
        computeParamsCustomStatusQuery({
            name: 'listAlertStatus',
            nameRef: 'get-all-alert-status',
            fetchOnStart: !!currentUser,
        }),
    );

    const { data: customCaseStatusList = [], refetch: refetchCaseCustomStatusList } = useListQuery<
        CustomStatusOutput[]
    >(
        computeParamsCustomStatusQuery({
            name: 'listCaseStatus',
            nameRef: 'get-all-case-status',
            fetchOnStart: !!currentUser,
        }),
    );

    useListQuery({
        queryParams: {
            selector: computeListSelector(),
        },
        controller: client.OrganisationController,
        manual: true,
        fetchOnStart: !!currentUser,
    });

    useListQuery({
        queryParams: {
            selector: computeProfileListSelector(),
        },
        controller: client.ProfileController,
        manual: true,
        fetchOnStart: !!currentUser,
    });

    useGenericQuery({
        fetchCallback: () => client.OrganisationController.getSharingProfile(),
        key: ['get-organisation-sharing-profile'],
        manual: true,
        fetchOnStart: !!currentUser,
    });

    useListQuery({
        controller: client.OrganisationController,
        queryParams: {
            selector: { head: [{ name: 'listVisibleUsers' }], name: 'get-visible-users' },
        },
        manual: true,
        fetchOnStart: !!currentUser,
    });

    const coloredTags = useMemo(
        () => ({
            ...taxonomyList?.reduce(
                (acc, { tags = [] }) => ({
                    ...acc,
                    ...tags.reduce(
                        (acc, { colour, ...tag }) =>
                            colour !== freeTagDefaultColour ? { ...acc, [formatTags(tag)]: colour } : acc,
                        {} as Record<string, string>,
                    ),
                }),
                {} as Record<string, string>,
            ),
            ...customTagList.reduce(
                (acc, { colour, ...tag }) => ({ ...acc, [formatTags(tag)]: colour }),
                {} as Record<string, string>,
            ),
        }),
        [taxonomyList, customTagList, freeTagDefaultColour],
    );

    const getStatusAlert = useCallback(
        (name: string) => {
            return customAlertStatusList.find(({ value }) => value === name) || null;
        },
        [customAlertStatusList],
    );

    const getStatusCase = useCallback(
        (name: string) => {
            return customCaseStatusList.find(({ value }) => value === name) || null;
        },
        [customCaseStatusList],
    );

    return (
        <ScopeContext.Provider
            value={{
                taxonomy: { list: taxonomyList || [], refetch: refetchTaxonomy },
                coloredTags,
                responderList,
                analyzerList,
                customFieldList,
                customStatus: {
                    alert: {
                        list: customAlertStatusList,
                        refetch: refetchAlertCustomStatusList,
                        getStatus: getStatusAlert,
                    },
                    case: {
                        list: customCaseStatusList,
                        refetch: refetchCaseCustomStatusList,
                        getStatus: getStatusCase,
                    },
                },
            }}
        >
            {children}
        </ScopeContext.Provider>
    );
};
